import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSettings,
  updateSettings,
  reindexAll,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  adminListFiles,
  approveFile,
  rejectFile,
  getFileSharedUsers,
  shareFileWithUsers,
  pullOllamaModel,
  getOllamaModels,
} from "../api/admin";
import { deleteFile, reindexFile } from "../api/files";
import { useToast } from "../context/ToastContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function PullModelSection() {
  const [model, setModel] = useState("");
  const [pulling, setPulling] = useState(false);
  const [status, setStatus] = useState(null); // {text, done, error}

  const handlePull = async () => {
    const name = model.trim();
    if (!name || pulling) return;
    setPulling(true);
    setStatus({ text: "Starting…", done: false, error: false });
    try {
      const res = await pullOllamaModel(name);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setStatus({ text: err.message || `Error ${res.status}`, done: false, error: true });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.error) {
              setStatus({ text: ev.error, done: false, error: true });
            } else if (ev.status) {
              const pct =
                ev.total > 0
                  ? ` (${Math.round((ev.completed / ev.total) * 100)}%)`
                  : "";
              const done = ev.status === "success";
              setStatus({ text: ev.status + pct, done, error: false });
            }
          } catch {}
        }
      }
    } catch (err) {
      setStatus({ text: err.message || "Failed", done: false, error: true });
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="space-y-2 pt-1">
      <label className="block text-xs text-dark-muted">Pull model from Ollama</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="e.g. llama3.2:3b"
          className="flex-1 rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
          disabled={pulling}
          onKeyDown={(e) => e.key === "Enter" && handlePull()}
        />
        <button
          onClick={handlePull}
          disabled={pulling || !model.trim()}
          className="rounded-lg border border-dark-border px-4 py-2 text-xs font-medium text-dark-text hover:border-[#555] transition-colors disabled:opacity-40"
        >
          {pulling ? "Pulling…" : "Pull"}
        </button>
      </div>
      {status && (
        <p
          className={`text-xs ${
            status.error
              ? "text-red-400"
              : status.done
              ? "text-green-400"
              : "text-dark-muted"
          }`}
        >
          {status.text}
        </p>
      )}
    </div>
  );
}

function Slider({ label, settingKey, min, max, step = 1, values, onChange }) {
  return (
    <div>
      <div className="flex justify-between mb-1.5">
        <label className="text-sm text-dark-text">{label}</label>
        <span className="text-sm text-accent font-mono">{values[settingKey]}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={values[settingKey] || min}
        onChange={(e) => onChange(settingKey, e.target.value)}
        className="slider-md"
      />
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        on ? "bg-accent" : "bg-dark-border"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
          on ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function SettingsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState("llm");
  const [values, setValues] = useState({});
  const [apiKeyInput, setApiKeyInput] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await getSettings();
      return res.data.data;
    },
  });

  const { data: ollamaModels } = useQuery({
    queryKey: ["ollama-models"],
    queryFn: async () => (await getOllamaModels()).data.data,
    enabled: values.llm_provider === "ollama",
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data) setValues(data);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (payload) => updateSettings(payload),
    onSuccess: () => toast("Settings saved", "success"),
    onError: () => toast("Failed to save settings", "error"),
  });

  const removeKeyMut = useMutation({
    mutationFn: () => updateSettings({ ...values, api_key: "" }),
    onSuccess: (res) => {
      setValues(res.data.data);
      setApiKeyInput("");
      toast("API key removed", "success");
    },
    onError: () => toast("Failed to remove API key", "error"),
  });

  const reindexMut = useMutation({
    mutationFn: reindexAll,
    onSuccess: () => toast("Re-indexing started in background", "success"),
    onError: () => toast("Failed to start re-index", "error"),
  });

  const set = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const handleSave = () => {
    const payload = { ...values };
    if (apiKeyInput) payload.api_key = apiKeyInput;
    else delete payload.api_key;

    const chunkSize = parseInt(payload.chunk_size ?? 512, 10);
    const chunkOverlap = parseInt(payload.chunk_overlap ?? 50, 10);
    if (chunkOverlap >= chunkSize) {
      toast("Chunk overlap must be less than chunk size", "error");
      return;
    }

    if (
      payload.llm_provider === "api" &&
      !apiKeyInput &&
      values.api_key !== "********"
    ) {
      toast("Add an API key before switching to API mode", "error");
      return;
    }

    saveMut.mutate(payload);
  };

  if (isLoading)
    return (
      <div className="flex h-screen items-center justify-center bg-dark-bg text-dark-muted text-sm">
        Loading…
      </div>
    );

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col">
      {/* Header */}
      <div className="mx-auto w-[90%] max-w-4xl w-full pt-10 pb-3 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="text-dark-muted hover:text-dark-text transition-colors"
          >
            <ion-icon name="arrow-back-outline" style={{ fontSize: "20px" }} />
          </button>
          <h1 className="text-base font-semibold text-dark-text">Settings</h1>
        </div>
        {tab === "llm" && (
          <button
            onClick={handleSave}
            disabled={saveMut.isPending}
            className="rounded-full bg-accent px-5 py-1.5 text-xs font-semibold text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex justify-center gap-1 border-b border-dark-border flex-shrink-0">
        {[
          { id: "llm", label: "LLM" },
          { id: "users", label: "Users" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-accent text-accent"
                : "border-transparent text-dark-muted hover:text-dark-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-[90%] max-w-4xl px-0 py-8 space-y-10">

          {/* ── LLM tab ── */}
          {tab === "llm" && (
            <>
              <section className="space-y-4">
                <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
                  LLM Provider
                </h2>
                <div className="flex gap-2">
                  {[
                    { v: "ollama", l: "Ollama (Local)" },
                    { v: "api", l: "API" },
                  ].map((p) => (
                    <button
                      key={p.v}
                      onClick={() => set("llm_provider", p.v)}
                      className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                        values.llm_provider === p.v
                          ? "bg-accent/15 text-accent border border-accent/25"
                          : "border border-dark-border text-dark-muted hover:border-[#555] hover:text-dark-text"
                      }`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>

                {values.llm_provider === "ollama" && (
                  <>
                    <div>
                      <label className="mb-1.5 block text-xs text-dark-muted">Model</label>
                      {ollamaModels?.length > 0 ? (
                        <select
                          value={values.llm_model || ""}
                          onChange={(e) => set("llm_model", e.target.value)}
                          className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
                        >
                          {ollamaModels.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={values.llm_model || ""}
                          onChange={(e) => set("llm_model", e.target.value)}
                          className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
                          placeholder="e.g. qwen2.5:3b — pull a model first"
                        />
                      )}
                    </div>
                    <PullModelSection />
                  </>
                )}

                {values.llm_provider === "api" && (
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1.5 block text-xs text-dark-muted">Provider</label>
                      <select
                        value={values.api_provider || ""}
                        onChange={(e) => set("api_provider", e.target.value)}
                        className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
                      >
                        <option value="anthropic">Anthropic (Claude)</option>
                        <option value="google">Google (Gemini)</option>
                        <option value="openai">OpenAI (GPT)</option>
                      </select>
                    </div>
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <label className="text-xs text-dark-muted">API Key</label>
                        {values.api_key === "********" && (
                          <button
                            type="button"
                            onClick={() => removeKeyMut.mutate()}
                            disabled={removeKeyMut.isPending}
                            className="text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                          >
                            {removeKeyMut.isPending ? "Removing…" : "Remove key"}
                          </button>
                        )}
                      </div>
                      <input
                        type="password"
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                        className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
                        placeholder={
                          values.api_key === "********"
                            ? "Leave blank to keep existing key"
                            : "Enter API key"
                        }
                      />
                    </div>
                  </div>
                )}
              </section>

              <section className="space-y-5">
                <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
                  RAG Settings
                </h2>
                <Slider label="Chunk size" settingKey="chunk_size" min={128} max={2048} step={64} values={values} onChange={set} />
                <Slider label="Chunk overlap" settingKey="chunk_overlap" min={0} max={256} step={16} values={values} onChange={set} />
                <Slider label="Top-K results" settingKey="top_k" min={1} max={20} values={values} onChange={set} />
                <Slider label="Similarity threshold" settingKey="similarity_threshold" min={0} max={1} step={0.05} values={values} onChange={set} />
              </section>

              <section className="space-y-4">
                <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
                  Access &amp; Sharing
                </h2>
                {[
                  { key: "allow_chat_sharing", label: "Allow chat sharing" },
                  { key: "require_admin_approval", label: "Require admin approval for uploads" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-dark-text">{label}</span>
                    <Toggle
                      on={values[key] === "true"}
                      onClick={() => set(key, values[key] === "true" ? "false" : "true")}
                    />
                  </div>
                ))}
              </section>

              <section className="space-y-3">
                <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
                  Maintenance
                </h2>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-dark-text">Re-index all files</p>
                    <p className="text-xs text-dark-muted mt-0.5">Rebuild the vector store from scratch</p>
                  </div>
                  <button
                    onClick={() => reindexMut.mutate()}
                    disabled={reindexMut.isPending}
                    className="rounded-lg border border-dark-border px-4 py-1.5 text-sm text-dark-text hover:border-[#555] transition-colors disabled:opacity-50"
                  >
                    {reindexMut.isPending ? "Starting…" : "Re-index"}
                  </button>
                </div>
              </section>

            </>
          )}

          {/* ── Files tab ── */}
          {tab === "files" && <FileManagementSection />}

          {/* ── Users tab ── */}
          {tab === "users" && <UserManagementSection />}

        </div>
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  indexed: "bg-green-900/40 text-green-400 border-green-800",
  pending: "bg-yellow-900/40 text-yellow-400 border-yellow-800",
  indexing: "bg-blue-900/40 text-blue-400 border-blue-800",
  failed: "bg-red-900/40 text-red-400 border-red-800",
  pending_approval: "bg-orange-900/40 text-orange-400 border-orange-800",
};

function ShareModal({ file, onClose }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);

  const { data: users } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await listUsers()).data.data,
  });

  const { data: sharedData } = useQuery({
    queryKey: ["file-shared", file.id],
    queryFn: async () => (await getFileSharedUsers(file.id)).data.data,
  });

  useEffect(() => {
    if (sharedData) setSelectedIds(sharedData);
  }, [sharedData]);

  const saveMut = useMutation({
    mutationFn: () => shareFileWithUsers(file.id, selectedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["file-shared", file.id] });
      toast("Sharing updated", "success");
      onClose();
    },
    onError: () => toast("Failed to update sharing", "error"),
  });

  const toggle = (uid) =>
    setSelectedIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );

  const nonAdminUsers = (users || []).filter((u) => u.role !== "admin");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-[400px] rounded-xl bg-dark-surface p-6 border border-dark-border" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-sm font-semibold text-dark-text">Share file</h3>
        <p className="mb-4 text-xs text-dark-muted truncate">{file.original_name}</p>

        <div className="space-y-2 max-h-56 overflow-y-auto mb-4">
          {nonAdminUsers.length === 0 && (
            <p className="text-xs text-dark-muted">No users to share with</p>
          )}
          {nonAdminUsers.map((u) => (
            <label key={u.id} className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded-lg hover:bg-[#2B2B30] transition-colors">
              <div className={`h-4 w-4 flex-shrink-0 flex items-center justify-center rounded border transition-colors ${selectedIds.includes(u.id) ? "bg-accent border-accent" : "border-[#444]"}`}>
                {selectedIds.includes(u.id) && (
                  <ion-icon name="checkmark" style={{ fontSize: "11px", color: "#fff" }} />
                )}
              </div>
              <input type="checkbox" className="hidden" checked={selectedIds.includes(u.id)} onChange={() => toggle(u.id)} />
              <div className="min-w-0">
                <p className="text-xs font-medium text-dark-text truncate">{u.name || u.username || u.email.split("@")[0]}</p>
                <p className="text-[10px] text-dark-muted truncate">{u.email}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-xs text-dark-muted hover:text-dark-text">Cancel</button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="rounded-lg bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FileManagementSection() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filterUser, setFilterUser] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [sharingFile, setSharingFile] = useState(null);

  const { data: allFiles } = useQuery({
    queryKey: ["admin-files"],
    queryFn: async () => (await adminListFiles()).data.data,
    refetchInterval: (q) => {
      const list = q.state.data || [];
      return list.some((f) => ["pending", "indexing"].includes(f.indexed_status)) ? 3000 : false;
    },
  });

  const delMut = useMutation({
    mutationFn: deleteFile,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-files"] }); qc.invalidateQueries({ queryKey: ["files"] }); toast("File deleted", "success"); },
    onError: () => toast("Failed to delete file", "error"),
  });
  const reindexMut = useMutation({
    mutationFn: reindexFile,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-files"] }); toast("Re-indexing started", "success"); },
    onError: () => toast("Re-index failed", "error"),
  });
  const approveMut = useMutation({
    mutationFn: approveFile,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-files"] }); toast("Approved", "success"); },
  });
  const rejectMut = useMutation({
    mutationFn: rejectFile,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-files"] }); toast("Rejected", "success"); },
  });

  const handleDelete = (f) => {
    if (!window.confirm(`Delete "${f.original_name}"?`)) return;
    delMut.mutate(f.id);
  };

  // Unique uploaders for filter dropdown
  const uploaders = [...new Map((allFiles || []).filter((f) => f.uploader_email).map((f) => [f.uploader_email, { email: f.uploader_email, name: f.uploader_name }])).values()];

  let files = [...(allFiles || [])];
  if (filterUser !== "all") files = files.filter((f) => f.uploader_email === filterUser);
  if (sortBy === "name") files.sort((a, b) => (a.original_name || "").localeCompare(b.original_name || ""));
  else if (sortBy === "type") files.sort((a, b) => a.file_type.localeCompare(b.file_type));
  // "newest" is default (already sorted by backend)

  return (
    <section className="space-y-4 pt-6 border-t border-dark-border">
      <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">File management</h2>

      {/* Filters */}
      <div className="flex gap-2">
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="rounded-lg bg-[#232323] border border-dark-border px-3 py-1.5 text-xs text-dark-text outline-none"
        >
          <option value="all">All users</option>
          {uploaders.map((u) => (
            <option key={u.email} value={u.email}>{u.name || u.email}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="rounded-lg bg-[#232323] border border-dark-border px-3 py-1.5 text-xs text-dark-text outline-none"
        >
          <option value="newest">Newest first</option>
          <option value="name">Name A–Z</option>
          <option value="type">Type</option>
        </select>
      </div>

      <div className="rounded-lg border border-dark-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-dark-surface text-xs text-dark-muted">
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Uploaded by</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="border-t border-dark-border">
                <td className="px-3 py-2 text-dark-text truncate max-w-[160px]">{f.original_name}</td>
                <td className="px-3 py-2 text-dark-muted uppercase text-xs">{f.file_type}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_BADGE[f.indexed_status] || ""}`}>
                    {f.indexed_status}
                  </span>
                </td>
                <td className="px-3 py-2 text-dark-muted text-xs truncate max-w-[120px]">
                  {f.uploader_name || "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {f.indexed_status === "pending_approval" ? (
                    <>
                      <button onClick={() => approveMut.mutate(f.id)} className="text-xs text-green-400 hover:text-green-300 mr-3">Approve</button>
                      <button onClick={() => rejectMut.mutate(f.id)} className="text-xs text-red-400 hover:text-red-300">Reject</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => reindexMut.mutate(f.id)} className="text-xs text-dark-muted hover:text-dark-text mr-3">Re-index</button>
                      <button onClick={() => setSharingFile(f)} className="text-xs text-blue-400 hover:text-blue-300 mr-3">Share</button>
                      <button onClick={() => handleDelete(f)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!files.length && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-dark-muted text-xs">No files yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {sharingFile && <ShareModal file={sharingFile} onClose={() => setSharingFile(null)} />}
    </section>
  );
}

function UserManagementSection() {
  const { user: me } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: users } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await listUsers()).data.data,
  });

  const delMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast("User deleted", "success");
    },
    onError: (err) =>
      toast(err.response?.data?.message || "Delete failed", "error"),
  });

  const handleDelete = (u) => {
    if (u.id === me?.id) {
      toast("You can't delete your own account", "error");
      return;
    }
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    delMut.mutate(u.id);
  };

  return (
    <section className="space-y-4 pt-6 border-t border-dark-border">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
          User management
        </h2>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-lg border border-dark-border px-3 py-1.5 text-xs font-medium text-dark-text hover:border-[#555] transition-colors"
        >
          + Create user
        </button>
      </div>

      <div className="rounded-lg border border-dark-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-dark-surface text-xs text-dark-muted">
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.id} className="border-t border-dark-border">
                <td className="px-3 py-2 text-dark-text truncate max-w-[180px]">{u.email}</td>
                <td className="px-3 py-2 text-dark-muted">{u.name || u.username || "—"}</td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      u.role === "admin"
                        ? "bg-accent/20 text-accent"
                        : "bg-dark-border text-dark-muted"
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setEditing(u)}
                    className="text-xs text-dark-muted hover:text-dark-text mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!users?.length && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-dark-muted text-xs">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <UserFormModal
          title="Create user"
          onClose={() => setShowCreate(false)}
          onSubmit={async (form) => {
            await createUser(form);
            qc.invalidateQueries({ queryKey: ["admin-users"] });
            toast("User created", "success");
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <UserFormModal
          title="Edit user"
          initial={editing}
          editMode
          onClose={() => setEditing(null)}
          onSubmit={async (form) => {
            await updateUser(editing.id, form);
            qc.invalidateQueries({ queryKey: ["admin-users"] });
            toast("User updated", "success");
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function UserFormModal({ title, initial, editMode, onClose, onSubmit }) {
  const [form, setForm] = useState({
    email: initial?.email || "",
    name: initial?.name || "",
    username: initial?.username || "",
    phone: initial?.phone || "",
    password: "",
    role: initial?.role || "user",
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (editMode) delete payload.password; // admin update doesn't change password
      await onSubmit(payload);
    } catch (err) {
      toast(err.response?.data?.message || "Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-[420px] rounded-xl bg-dark-surface p-6 border border-dark-border"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-sm font-semibold text-dark-text">{title}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} required />
          <Input label="Name" value={form.name} onChange={(v) => set("name", v)} />
          <Input label="Username" value={form.username} onChange={(v) => set("username", v)} />
          <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
          {!editMode && (
            <Input label="Password" type="password" value={form.password} onChange={(v) => set("password", v)} required />
          )}
          <div>
            <label className="mb-1 block text-xs text-dark-muted">Role</label>
            <select
              value={form.role}
              onChange={(e) => set("role", e.target.value)}
              className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-1.5 text-xs text-dark-muted hover:text-dark-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-dark-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
      />
    </div>
  );
}
